import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { Alert } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminAlertListGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<Alert>>,
    'queryKey' | 'queryFn'
  >;
}

interface AdminAlertListGetParams {
  limit: number;
  offset: number;
  sort: string;
  search: string;
  projectId: number;
  projectSectionId: number;
  contractorId: number;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  search: 'string',
  projectId: 'number',
  projectSectionId: 'number',
  contractorId: 'number',
}

export const useAlertListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminAlertListGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<unknown, Error, APIList<Alert>>(
    [Entities.Alerts, JSON.stringify(transformedParams)],
    async () => {
      const { limit, offset, sort, search, projectId, projectSectionId, contractorId } = queryParams
      const { data } = await api.v1AdminAlertsGet(
        limit,
        offset,
        sort,
        search,
        projectId,
        projectSectionId,
        contractorId,
      )
      return data
    },
    options,
  )
}
